import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ReactMarkdown from 'react-markdown';
import { useEffect, useState } from 'react';
import { faqs, essentialFaqs, type LocalizedFAQ, type FAQ } from '@/content/faqs';
import { useTranslation } from 'react-i18next';
import { type SupportedLanguage } from '@/lib/i18n';

interface FaqAccordionProps {
  showAll?: boolean;
}

const FaqAccordion = ({ showAll = false }: FaqAccordionProps) => {
  const [markdownContent, setMarkdownContent] = useState<{ [key: string]: string }>({});
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as SupportedLanguage;
  const allFaqs = showAll ? [...essentialFaqs, ...faqs] : essentialFaqs;
  const getFaqData = (faq: LocalizedFAQ): FAQ | undefined => {
    return faq[currentLang] || faq['en'];
  };

  const displayFaqs = allFaqs
    .map(faq => ({ faq, data: getFaqData(faq) }))
    .filter((item): item is { faq: LocalizedFAQ; data: FAQ } => !!item.data);

  useEffect(() => {
    const loadMarkdownContent = async () => {
      const contentMap: { [key: string]: string } = {};

      for (const item of displayFaqs) {
        try {
          const response = await fetch(item.data.answerFile);
          const content = await response.text();
          const year = String(new Date().getFullYear());
          contentMap[item.data.answerFile] = content.replace(/\{\{YEAR\}\}/g, year);
        } catch (error) {
          console.error(`Error loading markdown for ${item.data.answerFile}:`, error);
          contentMap[item.data.answerFile] = t('faq.error');
        }
      }

      setMarkdownContent(contentMap);
    };

    loadMarkdownContent();
  }, [displayFaqs, t]);

  return (
    <Accordion type="multiple" className="space-y-2 md:space-y-3">
      {displayFaqs.map((item, index) => (
        <AccordionItem
          key={index}
          value={`item-${index}`}
          className="border border-slate-200 rounded-xl px-5 md:px-7 py-0 md:py-2 hover:md:shadow-md transition-shadow duration-300 ease-in-out bg-white"
        >
          <AccordionTrigger className="text-left text-base md:text-xl font-semibold text-slate-900 hover:no-underline">
            <span className="pr-4">
              {item.data.question}
            </span>
          </AccordionTrigger>
          <AccordionContent className="text-slate-600 py-4">
            {markdownContent[item.data.answerFile] ? (
              <div className="prose prose-slate prose-p:mt-2 prose-p:mb-4 prose-p:text-slate-600 prose-p:leading-relaxed max-w-none">
                <ReactMarkdown>
                  {markdownContent[item.data.answerFile]}
                </ReactMarkdown>
              </div>
            ) : (
              <p>{t('common.loading')}</p>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default FaqAccordion;
